import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getStepTypeConfig } from '../config/stepTypes';
import PageHeader from '../../../components/common/PageHeader';
import { useNavigationStack } from '../../../context/NavigationStackContext';

export default function ProcedureCatalogPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { t } = useTranslation(['procedures', 'common']);

  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load all procedures from template file
  const loadProcedures = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const configResponse = await fetch('/procedure-templates/config.json');
      if (!configResponse.ok) {
        throw new Error(t('procedures:catalog.errors.failed_load_categories'));
      }

      const config = await configResponse.json();
      const allProcedures = [];

      // Load all procedure files from config
      for (const [, fileName] of Object.entries(config)) {
        try {
          const response = await fetch(`/procedure-templates/${fileName}`);
          if (response.ok) {
            const data = await response.json();
            allProcedures.push(...data);
          }
        } catch (err) {
          console.error(`Failed to load ${fileName}:`, err);
        }
      }

      // Sort procedures by name
      allProcedures.sort((a, b) => a.name.localeCompare(b.name));
      setProcedures(allProcedures);
    } catch (err) {
      setError(
        t('procedures:catalog.errors.failed_load_catalog', {
          message: err.message,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load procedures on mount
  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  // Filter procedures based on search
  const filteredProcedures = useMemo(() => {
    if (!debouncedSearch) {
      return procedures;
    }

    const lowerSearch = debouncedSearch.toLowerCase();
    return procedures.filter(
      (procedure) =>
        procedure.name.toLowerCase().includes(lowerSearch) ||
        procedure.description.toLowerCase().includes(lowerSearch)
    );
  }, [procedures, debouncedSearch]);

  const handleRefresh = () => {
    loadProcedures();
  };

  const handleProcedureClick = (procedure) => {
    setSelectedProcedure(procedure);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedProcedure(null);
  };

  const handleUseTemplate = (procedure) => {
    // Navigate to create procedure page with template data in state
    navigate('/procedures/create', {
      state: {
        templateProcedure: procedure,
      },
    });
    handleCloseDetails();
  };

  const toggleCardExpansion = (procedureId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [procedureId]: !prev[procedureId],
    }));
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const getStepTypeLabel = (stepType) => {
    return getStepTypeConfig(stepType)?.label || stepType;
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={() => navigateBack('/procedures')}
        title={t('procedures:catalog.title')}
        subtitle={t('procedures:catalog.subtitle')}
        actions={<>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </>}
      />

      {/* Search Bar */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder={t('procedures:catalog.search.placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {/* Procedure Cards Grid */}
      {!loading && filteredProcedures.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))"
          gap={3}
        >
          {filteredProcedures.map((procedure) => (
            <Card
              key={procedure.id}
              sx={{
                height: 'fit-content',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleProcedureClick(procedure);
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                      flex: 1,
                      pr: 1,
                    }}
                  >
                    {procedure.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCardExpansion(procedure.id);
                    }}
                  >
                    {expandedCards[procedure.id] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>

                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  <Chip
                    icon={<CategoryIcon />}
                    label={t('procedures:catalog.steps_count', { count: procedure.steps?.length || 0 })}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Collapse in={expandedCards[procedure.id]} timeout="auto">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-line',
                      mb: 2,
                    }}
                  >
                    {procedure.description}
                  </Typography>

                  {procedure.steps && procedure.steps.length > 0 && (
                    <Box mt={2}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                      >
                        {t('procedures:catalog.steps_heading')}:
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {procedure.steps.map((step, index) => (
                          <Typography
                            key={`${procedure.id}-step-${step.step_order}`}
                            variant="caption"
                            color="text.secondary"
                          >
                            {index + 1}. {step.label} ({getStepTypeLabel(step.step_type)})
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Collapse>

                {!expandedCards[procedure.id] && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                    }}
                  >
                    {procedure.description}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProcedureClick(procedure);
                  }}
                >
                  {t('procedures:catalog.actions.view_details')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUseTemplate(procedure);
                  }}
                >
                  {t('procedures:catalog.actions.use_template')}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!loading && filteredProcedures.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {debouncedSearch
              ? t('procedures:catalog.empty.no_search_results')
              : t('procedures:catalog.empty.no_procedures')}
          </Typography>
          {debouncedSearch && (
            <Button onClick={handleClearSearch} sx={{ mt: 2 }}>
              {t('procedures:catalog.search.clear')}
            </Button>
          )}
        </Box>
      )}

      {/* Procedure Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedProcedure?.name}</DialogTitle>
        <DialogContent>
          {selectedProcedure && (
            <Box>
              <Box mb={3}>
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  <Chip
                    icon={<CategoryIcon />}
                    label={t('procedures:catalog.steps_count', { count: selectedProcedure.steps?.length || 0 })}
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                {t('common:labels.description')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ whiteSpace: 'pre-line' }}
              >
                {selectedProcedure.description}
              </Typography>

              {selectedProcedure.steps && selectedProcedure.steps.length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    {t('procedures:catalog.dialog.procedure_steps')}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {selectedProcedure.steps.map((step, index) => (
                      <Card
                        key={`${selectedProcedure.id}-step-${step.step_order}`}
                        variant="outlined"
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            justifyContent="space-between"
                          >
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {t('procedures:catalog.step_number', { number: index + 1 })}: {step.label}
                              </Typography>
                              <Chip
                                label={getStepTypeLabel(step.step_type)}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                            {step.is_required && (
                              <Chip
                                label={t('procedures:labels.required')}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          {step.description && (
                            <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                              {step.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            {t('common:buttons.close')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleUseTemplate(selectedProcedure)}
          >
            {t('procedures:catalog.actions.use_template')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
